import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Inbox, Loader2, Lock, LogOut } from "lucide-react";
import { API } from "@/lib/api";

const STORAGE_KEY = "dc_admin_key";

const roleColor = (role) =>
  role === "Agency / Caseworker" ? "bg-clay/10 text-clay border-clay/30" : role === "Social Worker" ? "bg-sage/10 text-sage border-sage/30" : "bg-gold/10 text-ink-muted border-gold/40";

export default function Admin() {
  const [key, setKey] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (k) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/messages`, { headers: { "X-Admin-Key": k } });
      setMessages(data.messages);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem(STORAGE_KEY);
        setKey("");
        setError("That key didn't work. Try again.");
      } else {
        setError("Could not load messages. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    const v = draft.trim();
    if (!v) return;
    sessionStorage.setItem(STORAGE_KEY, v);
    setKey(v);
    setDraft("");
    load(v);
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setKey("");
    setMessages([]);
  };

  if (!key) {
    return (
      <main className="min-h-screen bg-paper flex items-center justify-center px-6" data-testid="admin-login">
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-sm rounded-3xl border border-ink/10 bg-paper-2 p-8"
        >
          <Lock className="h-8 w-8 text-clay" />
          <h1 className="mt-4 font-serif text-2xl font-semibold tracking-tight">Inquiries Inbox</h1>
          <p className="mt-2 text-sm text-ink-muted">Enter your admin key to view contact form messages.</p>
          <input
            type="password"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Admin key"
            autoComplete="off"
            className="mt-6 w-full rounded-xl border border-ink/15 bg-paper px-4 py-3 text-sm outline-none focus:border-clay focus:ring-2 focus:ring-clay/20 transition-all duration-300"
            data-testid="admin-key-input"
          />
          {error && <p className="mt-3 text-sm text-clay" data-testid="admin-error">{error}</p>}
          <button
            type="submit"
            className="mt-5 w-full rounded-full bg-ink py-3 text-sm font-medium text-paper hover:bg-clay transition-colors duration-300"
            data-testid="admin-login-button"
          >
            Open Inbox
          </button>
        </motion.form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-paper" data-testid="admin-inbox">
      <div className="max-w-4xl mx-auto px-6 py-14">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Inbox className="h-6 w-6 text-clay" />
            <h1 className="font-serif text-3xl font-semibold tracking-tight">Inquiries</h1>
            <span className="font-mono text-xs text-ink-light" data-testid="admin-count">{messages.length} total</span>
          </div>
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2 text-sm text-ink-muted hover:border-clay hover:text-clay transition-colors duration-300"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-4 w-4" /> Forget key
          </button>
        </div>
        {loading && <Loader2 className="mt-10 h-6 w-6 animate-spin text-clay" />}
        {error && <p className="mt-6 text-sm text-clay" data-testid="admin-error">{error}</p>}
        <div className="mt-8 space-y-4" data-testid="admin-messages-list">
          {messages.map((m) => (
            <div key={m.id} className="rounded-2xl border border-ink/10 bg-paper-2 p-6" data-testid="admin-message-item">
              <div className="flex flex-wrap items-center gap-3">
                <p className="font-semibold text-sm">{m.name}</p>
                <a href={`mailto:${m.email}`} className="text-sm text-clay hover:text-clay-dark transition-colors">{m.email}</a>
                <span className={`rounded-full border px-3 py-0.5 text-[11px] font-mono ${roleColor(m.role)}`}>{m.role}</span>
                <span className="ml-auto font-mono text-[11px] text-ink-light">
                  {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
                </span>
              </div>
              {(m.team_size || m.caseload) && (
                <p className="mt-2 text-xs font-mono text-ink-muted">
                  Team: {m.team_size || "—"} · Active families: {m.caseload || "—"}
                </p>
              )}
              <p className="mt-3 text-sm text-ink-muted leading-relaxed whitespace-pre-wrap">{m.message}</p>
            </div>
          ))}
          {!loading && messages.length === 0 && (
            <p className="text-sm text-ink-muted" data-testid="admin-empty">No inquiries yet — new contact form messages will appear here.</p>
          )}
        </div>
      </div>
    </main>
  );
}
